import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Heart, Menu, X, Lock, User } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { CURRENCIES } from "@/i18n";

const Toggle = ({ options, value, onChange, testid, tone = "dark" }) => {
  const active = tone === "dark" ? "text-paper" : "text-ink";
  const inactive = tone === "dark" ? "text-paper/50 hover:text-paper" : "text-ink/55 hover:text-ink";
  const sep = tone === "dark" ? "text-paper/25" : "text-ink/30";
  return (
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid={testid}>
      {options.map((opt, i) => (
        <React.Fragment key={opt}>
          {i > 0 && <span className={sep}>/</span>}
          <button
            data-testid={`${testid}-${opt.toLowerCase()}`}
            onClick={() => onChange(opt)}
            className={`transition-colors duration-300 ${value === opt ? active : inactive}`}
          >
            {opt}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

export default function Navbar() {
  const { t, lang, setLang, currency, setCurrency, cart, setCartOpen, wishlist, setWishlistOpen, customer } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const links = [
    { to: "/catalogue", label: t.nav.catalogue },
    { to: "/about", label: t.nav.about },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-ink text-paper border-b border-white/10 ${
          scrolled ? "shadow-[0_6px_24px_-8px_rgba(0,0,0,0.5)]" : ""
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex items-center justify-between px-4 md:px-10 h-16 md:h-20">
          {/* Left: toggles (desktop) */}
          <div className="hidden md:flex items-center gap-6 flex-1">
            <Toggle options={["RU", "EN"]} value={lang.toUpperCase()} onChange={(v) => setLang(v.toLowerCase())} testid="lang-toggle" />
            <span className="h-4 w-px bg-paper/25" />
            <Toggle options={Object.keys(CURRENCIES)} value={currency} onChange={setCurrency} testid="currency-toggle" />
          </div>

          {/* Center: wordmark */}
          <Link to="/" className="flex items-center md:flex-1 md:justify-center group" data-testid="logo-link">
            <span className="font-display text-base md:text-2xl tracking-[0.12em] md:tracking-[0.25em] uppercase leading-none whitespace-nowrap">
              Heda&nbsp;Gelogaeva
            </span>
          </Link>

          {/* Right: nav + cart */}
          <div className="flex items-center justify-end gap-4 md:gap-6 flex-1">
            <nav className="hidden md:flex items-center gap-7 text-xs uppercase tracking-[0.2em]">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.to.replace("/", "")}`}
                  className={`link-underline whitespace-nowrap transition-colors ${location.pathname === l.to ? "text-paper" : "text-paper/60 hover:text-paper"}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/contact"
                data-testid="nav-contact"
                className={`link-underline transition-colors uppercase tracking-[0.2em] ${location.pathname === "/contact" ? "text-paper" : "text-paper/60 hover:text-paper"}`}
              >
                {t.inquiry.open}
              </Link>
            </nav>
            <button onClick={() => setWishlistOpen(true)} className="relative" data-testid="open-wishlist-button" aria-label="Open favourites">
              <Heart className="h-5 w-5" strokeWidth={1.2} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 h-4 w-4 bg-paper text-ink text-[10px] flex items-center justify-center rounded-full font-sans" data-testid="wishlist-count">
                  {wishlist.length}
                </span>
              )}
            </button>
            <button onClick={() => setCartOpen(true)} className="relative" data-testid="open-cart-button" aria-label="Open selection">
              <ShoppingBag className="h-5 w-5" strokeWidth={1.2} />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 h-4 w-4 bg-paper text-ink text-[10px] flex items-center justify-center rounded-full font-sans" data-testid="cart-count">
                  {cart.length}
                </span>
              )}
            </button>
            <Link to="/account" data-testid="nav-account" aria-label="My account" className={`relative transition-colors ${location.pathname === "/account" ? "text-paper" : "text-paper/70 hover:text-paper"}`}>
              <User className="h-5 w-5" strokeWidth={1.2} />
              {customer && <span className="absolute -top-1.5 -right-1.5 h-2 w-2 bg-[var(--gold-deep)] rounded-full" data-testid="account-signedin-dot" />}
            </Link>
            <Link to="/admin/login" data-testid="admin-login-link" aria-label="Owner login" className="text-paper/70 hover:text-paper transition-colors">
              <Lock className="h-[18px] w-[18px]" strokeWidth={1.2} />
            </Link>
            <button className="md:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Menu">
              <Menu className="h-6 w-6" strokeWidth={1.2} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-paper flex flex-col p-6 md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-end">
              <button onClick={() => setMobileOpen(false)} aria-label="Close"><X className="h-7 w-7" strokeWidth={1.2} /></button>
            </div>
            <nav className="flex flex-col gap-6 mt-16">
              {links.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}>
                  <Link to={l.to} className="font-display text-5xl tracking-tight">{l.label}</Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + links.length * 0.08 }}>
                <Link
                  to="/contact"
                  onClick={() => setMobileOpen(false)}
                  data-testid="nav-contact-mobile"
                  className="font-display text-5xl tracking-tight text-left block"
                >
                  {t.inquiry.open}
                </Link>
              </motion.div>
            </nav>
            <div className="mt-auto flex flex-col gap-5 pt-10 border-t border-line">
              <Toggle options={["RU", "EN"]} value={lang.toUpperCase()} onChange={(v) => setLang(v.toLowerCase())} testid="lang-toggle-mobile" tone="light" />
              <Toggle options={Object.keys(CURRENCIES)} value={currency} onChange={setCurrency} testid="currency-toggle-mobile" tone="light" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
